/**
 * DemoExperience — the operator-console redesign of the per-device and
 * fleet-wide demo analytics surface. Shared by:
 *
 *   - DeviceDetail.tsx        passes `thing` → scoped to one device,
 *                             drill-down click affordances enabled.
 *   - Analytics.tsx           omits `thing` → fleet-wide, no drill-downs.
 *
 * Visual vocabulary (extends the state-timeline language):
 *   - Single card with hairline rules between zones, instead of a 2×2
 *     grid of card-in-card chrome.
 *   - SF Mono for every number; Inter for labels.
 *   - KPI band on top with ▲/▼ deltas vs the equivalent prior window.
 *   - Sessions-per-day bar chart (kept), polished separately in D3.
 *   - Calendar heatmap (added in D4) — reserved slot below the bars.
 *   - 3-column breakdown of Top views / Sources / Content at the
 *     bottom, hairline-ruled rows with right-aligned tabular numerics.
 */
import { Fragment, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useAnalyticsContent,
  useAnalyticsProductFeatures,
  useAnalyticsProducts,
  useAnalyticsSessions,
  useAnalyticsTopViews,
  useDeviceSessions,
  type ContentRow,
  type DemoSessionRow,
  type FeatureRow,
  type ProductRow,
  type SessionDay,
  type TopView,
} from '../api/hooks';
import type { AnalyticsFilters } from '../api/hooks';
import { ActivityHeatmap } from './ActivityHeatmap';
import {
  fmtAgo,
  fmtDuration,
  fmtMs,
  fmtPct,
  ulidTimestampMs,
  useNow,
} from './format';
import { BreakdownTable, KPITile, SkeletonLine } from './kpis';

const VALID_DAYS = [7, 30, 90] as const;
type DaysOption = (typeof VALID_DAYS)[number];

// ─── URL-backed days selector ────────────────────────────────────────
//
// Drives the time-range buttons. Reload-safe and shareable: hitting
// /devices/foo?days=90 lands on the 90 d window directly. The default
// (30 d) is encoded as an absent param to keep typical URLs clean.

function useDaysUrlState(): {
  days: DaysOption;
  setDays: (n: DaysOption) => void;
} {
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get('days'));
  const days = (VALID_DAYS as readonly number[]).includes(raw)
    ? (raw as DaysOption)
    : 30;
  const setDays = (n: DaysOption) => {
    const next = new URLSearchParams(params);
    if (n === 30) next.delete('days');
    else next.set('days', String(n));
    setParams(next, { replace: true });
  };
  return { days, setDays };
}

// ─── Polished bar chart (D3) ────────────────────────────────────────
//
// Inline rather than reusing BarChart from format.tsx because the
// console aesthetic wants different chrome (taller, thinner bars,
// crosshair on hover, white styled tooltip matching the timeline's).
// Keeping it local also unblocks D4 — the heatmap can share the same
// tooltip primitive without coupling to a general-purpose chart prim.

interface BarPoint<M> {
  label: string;
  value: number;
  meta: M;
}

function ConsoleBarChart<M>({
  data,
  height = 120,
  tooltip,
  onBarClick,
}: {
  data: Array<BarPoint<M>>;
  height?: number;
  tooltip?: (item: BarPoint<M>) => React.ReactNode;
  onBarClick?: (item: BarPoint<M>) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<{
    index: number;
    xPx: number; // for HTML tooltip positioning (relative to container)
    xPct: number; // for SVG crosshair (centre of the hovered bar)
  } | null>(null);

  if (data.length === 0) {
    return <p className="text-xs text-muted">No data in this window.</p>;
  }

  const max = Math.max(...data.map((d) => d.value), 1);
  // Bar geometry — thin bars with 1.5 px gap (approximate at typical
  // chart widths; SVG viewBox stretches so the gap reads consistently).
  const slot = 100 / data.length;
  // Gap takes ~25% of the slot at most; falls below 1.5 px equivalent
  // only when there are very few bars, which is fine.
  const barW = Math.max(slot * 0.75, 0.6);
  const interactive = Boolean(tooltip || onBarClick);
  // X-axis labels render as HTML overlay below the SVG so they don't
  // get stretched by preserveAspectRatio="none". Choose a stride so
  // ~8 labels are drawn at most regardless of column count.
  const labelStride = Math.max(1, Math.ceil(data.length / 8));

  return (
    <div ref={containerRef} className="relative">
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="w-full block"
        style={{ height }}
      >
        {/* Baseline rule */}
        <line
          x1={0}
          x2={100}
          y1={height}
          y2={height}
          stroke="#E4E4E4"
          strokeWidth={0.4}
          vectorEffect="non-scaling-stroke"
        />
        {data.map((d, i) => {
          const h = (d.value / max) * (height - 2);
          const x = i * slot + (slot - barW) / 2;
          const y = height - h;
          return (
            <g key={`${d.label}-${i}`}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                rx={0.4}
                className={
                  'fill-accent/85' +
                  (interactive ? ' cursor-pointer hover:fill-accent' : '')
                }
                onMouseEnter={
                  interactive
                    ? (e) => {
                        const rect =
                          containerRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        setHover({
                          index: i,
                          xPx: e.clientX - rect.left,
                          xPct: i * slot + slot / 2,
                        });
                      }
                    : undefined
                }
                onMouseMove={
                  interactive
                    ? (e) => {
                        const rect =
                          containerRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        setHover({
                          index: i,
                          xPx: e.clientX - rect.left,
                          xPct: i * slot + slot / 2,
                        });
                      }
                    : undefined
                }
                onMouseLeave={interactive ? () => setHover(null) : undefined}
                onClick={onBarClick ? () => onBarClick(d) : undefined}
              />
            </g>
          );
        })}

        {/* Crosshair — sits over the hovered bar's centre. */}
        {hover && (
          <line
            x1={hover.xPct}
            x2={hover.xPct}
            y1={0}
            y2={height}
            stroke="#1C1B1F"
            strokeWidth={0.3}
            vectorEffect="non-scaling-stroke"
            opacity={0.4}
          />
        )}
      </svg>

      {/* X-axis labels — HTML overlay so the typography isn't stretched
          by preserveAspectRatio="none". Sparse: every Nth column. We
          also show the last column's label, but only when it's far
          enough from the previous stride-aligned label to avoid the
          two collapsing on top of each other. */}
      <div
        aria-hidden="true"
        className="relative h-4 mt-1 text-[10px] text-muted tabular-nums"
      >
        {data.map((d, i) => {
          const onStride = i % labelStride === 0;
          const isLast = i === data.length - 1;
          // Distance (in columns) from this index to the most recent
          // stride-aligned position. Only renders the last if it's
          // at least half a stride beyond — otherwise it collides
          // with the previous stride label.
          const distFromStride = i % labelStride;
          if (!onStride && !(isLast && distFromStride >= labelStride / 2)) {
            return null;
          }
          return (
            <span
              key={`label-${i}`}
              className="absolute -translate-x-1/2"
              style={{ left: `${(i * slot + slot / 2)}%` }}
            >
              {d.label}
            </span>
          );
        })}
      </div>

      {/* Styled HTML tooltip — matches the timeline's tooltip vocabulary
          (white card, hairline border, small shadow, pointer-events-none). */}
      {tooltip && hover && data[hover.index] && (
        <div
          role="tooltip"
          className="absolute z-10 -translate-x-1/2 -translate-y-full bg-white border border-border rounded-md shadow-lg px-2.5 py-1.5 text-[10px] text-fg tabular-nums whitespace-nowrap pointer-events-none"
          style={{
            // Clamp horizontally so the tooltip stays inside the chart.
            left: (() => {
              const cw = containerRef.current?.clientWidth ?? 0;
              const halfW = 90; // ~tooltip half-width approx; clamp is
                                // generous, real measurement deferred.
              return Math.max(halfW, Math.min(cw - halfW, hover.xPx));
            })(),
            top: -6,
          }}
        >
          {tooltip(data[hover.index]!)}
        </div>
      )}
    </div>
  );
}

// ─── KPI computation ─────────────────────────────────────────────────

interface KpiTotals {
  sessions: number;
  activeS: number;
  views: number;
  feedback: number;
}

function totals(rows: SessionDay[]): KpiTotals {
  return rows.reduce<KpiTotals>(
    (acc, r) => ({
      sessions: acc.sessions + r.sessions,
      activeS: acc.activeS + Number(r.total_active_s ?? 0),
      views: acc.views + r.total_views,
      feedback: acc.feedback + r.feedback_count,
    }),
    { sessions: 0, activeS: 0, views: 0, feedback: 0 },
  );
}

// UTC midnight for a Date — keeps day keys stable regardless of the
// browser's local timezone.
function utcDayKey(d: Date): string {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  )
    .toISOString()
    .slice(0, 10);
}

// Fill missing days with zero-value rows so the bar chart always
// renders one bar per day in the window — backends that return sparse
// rows (e.g. only days with at least one session) would otherwise
// produce a chart with fewer bars than the selected range. ASC order
// (oldest → newest); newest day is `now` (UTC midnight).
function fillDays(rows: SessionDay[], days: number): SessionDay[] {
  const byDay = new Map<string, SessionDay>();
  for (const r of rows) {
    const key = (r.day ?? '').slice(0, 10);
    byDay.set(key, r);
  }
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const out: SessionDay[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.valueOf());
    d.setUTCDate(d.getUTCDate() - i);
    const key = utcDayKey(d);
    out.push(
      byDay.get(key) ?? {
        day: d.toISOString(),
        sessions: 0,
        total_active_s: '0',
        avg_active_s: '0',
        total_views: 0,
        feedback_count: 0,
      },
    );
  }
  return out;
}

/**
 * Comparison vs prior window. Returns `null` when the prior window
 * is empty AND the current window isn't — that's "first data" rather
 * than "+100 %", which a stack of ▲100 % deltas would misleadingly
 * suggest on a brand-new device. Returns a flat delta when both
 * windows are zero (true no-change), so the tile reads cleanly.
 */
function deltaPct(
  current: number,
  prior: number,
):
  | { pct: number; direction: 'up' | 'down' | 'flat' }
  | null {
  if (prior === 0 && current === 0) return { pct: 0, direction: 'flat' };
  if (prior === 0) return null;
  const pct = ((current - prior) / prior) * 100;
  if (Math.abs(pct) < 0.5) return { pct: 0, direction: 'flat' };
  return { pct: Math.abs(pct), direction: pct > 0 ? 'up' : 'down' };
}

// ─── Inline session drill-down panel (F4) ───────────────────────────

function fmtHHMM(d: Date | null): string {
  if (!d) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function deriveSessionStart(row: DemoSessionRow): Date | null {
  const ms = ulidTimestampMs(row.session_id);
  if (ms !== null) return new Date(ms);
  // Fallback: backend's session_id isn't a parseable ULID — derive
  // start from ended-ts minus (active + suspended). Tolerable when
  // the device's clock is sane.
  const ended = new Date(row.ts).getTime();
  const active = Number(row.data.active_s ?? 0);
  const susp = Number(row.data.suspended_s ?? 0);
  if (!Number.isFinite(active) || !Number.isFinite(susp)) return null;
  return new Date(ended - (active + susp) * 1000);
}

function SessionDayPanel({
  thing,
  day,
  onClose,
}: {
  thing: string;
  day: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const q = useDeviceSessions(thing, day);
  const dayDate = new Date(day);
  const dayLabel = Number.isNaN(dayDate.getTime())
    ? day
    : dayDate.toLocaleDateString([], {
        weekday: 'long',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
  const rows = q.data?.sessions ?? [];
  return (
    <div className="mt-5 -mx-4 px-4 py-3 bg-soft/40 border-y border-border">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="text-sm font-medium tracking-tight">
          Sessions on{' '}
          <span className="text-fg">{dayLabel}</span>
          {q.data && (
            <span className="ml-2 text-[11px] text-muted tabular-nums">
              {rows.length} {rows.length === 1 ? 'session' : 'sessions'}
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-muted hover:text-fg text-xs px-2 py-0.5 rounded"
          aria-label="Close session day panel"
        >
          ✕ Close
        </button>
      </div>
      {q.isLoading && (
        <div className="h-12 bg-soft rounded animate-pulse" />
      )}
      {q.error && (
        <p className="text-xs text-warn">{(q.error as Error).message}</p>
      )}
      {q.data && rows.length === 0 && (
        <p className="text-xs text-muted">No sessions on this day.</p>
      )}
      {q.data && rows.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted border-b border-border">
              <th
                className="font-medium py-1.5 pr-2 cursor-help"
                title="Wall-clock time the demo session began."
              >
                Started
              </th>
              <th
                className="font-medium py-1.5 pr-2 cursor-help"
                title="Wall-clock time the demo session ended (when the device left demo mode)."
              >
                Ended
              </th>
              <th
                className="font-medium py-1.5 pr-2 text-right cursor-help"
                title="Time the device was actively demoing — sys_state=LampOn and demo_mode=active."
              >
                Active
              </th>
              <th
                className="font-medium py-1.5 pr-2 text-right cursor-help"
                title="Time the demo was paused / suspended (lamp on, but demo_mode=suspended). Excluded from Active."
              >
                Suspended
              </th>
              <th
                className="font-medium py-1.5 pr-2 text-right cursor-help"
                title="Number of QML views the user navigated to during the session."
              >
                Views
              </th>
              <th
                className="font-medium py-1.5 pr-2 text-right cursor-help"
                title="Number of content plays (videos / audio) started during the session."
              >
                Plays
              </th>
              <th
                className="font-medium py-1.5 pr-2 cursor-help"
                title="Did the user reach the feedback view at the end of the session?"
              >
                Feedback
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const start = deriveSessionStart(s);
              const end = new Date(s.ts);
              const href = `/devices/${encodeURIComponent(thing)}/sessions/${encodeURIComponent(s.session_id)}`;
              return (
                <tr
                  key={s.event_id}
                  onClick={() => navigate(href)}
                  className="border-b border-border last:border-0 cursor-pointer hover:bg-soft/60"
                >
                  <td className="py-1.5 pr-2 tabular-nums">{fmtHHMM(start)}</td>
                  <td className="py-1.5 pr-2 tabular-nums">{fmtHHMM(end)}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">
                    {fmtDuration(Number(s.data.active_s ?? 0))}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">
                    {fmtDuration(Number(s.data.suspended_s ?? 0))}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">
                    {s.data.view_count ?? 0}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">
                    {s.data.content_play_count ?? 0}
                  </td>
                  <td className="py-1.5 pr-2">
                    {s.data.reached_feedback ? '✓' : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Inline product drill-down panel ────────────────────────────────
//
// Mirror of SessionDayPanel: clicking a product row in the Product
// engagement table expands a per-pill (feature_view) breakdown beneath
// the table, rather than navigating away. Skipped on the global
// /analytics page (no thing context). Uses useAnalyticsProductFeatures
// which is gated on a non-null productModel — so the panel doesn't
// fire a request until the user opens it.

function ProductFeaturePanel({
  thing,
  productModel,
  days,
  filters,
  onClose,
}: {
  /** `undefined` on the fleet-wide Analytics view → backend rolls up
      feature_view events across the entire tenant. */
  thing?: string;
  productModel: string;
  days: number;
  filters?: AnalyticsFilters;
  onClose: () => void;
}) {
  const q = useAnalyticsProductFeatures(productModel, days, thing, filters);
  const rows = q.data?.features ?? [];
  return (
    // The wrapper used to break out of the parent card's padding via
    // `mt-4 -mx-4`, but the panel now renders inside a colSpan'd table
    // cell — the cell provides its own bounds, so the negative margins
    // would push the soft tint outside the row visually.
    <div className="px-3 py-3 bg-soft/40">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="text-sm font-medium tracking-tight">
          Feature dialogs for{' '}
          <span className="font-mono text-fg">{productModel}</span>
          {q.data && (
            <span className="ml-2 text-[11px] text-muted tabular-nums">
              {rows.length} {rows.length === 1 ? 'pill' : 'pills'}
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-muted hover:text-fg text-xs px-2 py-0.5 rounded"
          aria-label="Close product feature panel"
        >
          ✕ Close
        </button>
      </div>
      {q.isLoading && (
        <div className="h-12 bg-soft rounded animate-pulse" />
      )}
      {q.error && (
        <p className="text-xs text-warn">{(q.error as Error).message}</p>
      )}
      {q.data && rows.length === 0 && (
        <p className="text-xs text-muted">
          No feature_view events for this product in the window.
        </p>
      )}
      {q.data && rows.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted border-b border-border">
              <th className="font-medium py-1.5 pr-2">Feature</th>
              <th className="font-medium py-1.5 pr-2 text-right">Views</th>
              <th className="font-medium py-1.5 pr-2 text-right">Avg dwell</th>
              <th className="font-medium py-1.5 pr-2 text-right">Total dwell</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f: FeatureRow) => (
              <tr
                key={f.pill_id}
                className="border-b border-border last:border-0"
              >
                <td className="py-1.5 pr-2">
                  <span className="text-fg">{f.pill_label}</span>{' '}
                  <span className="text-muted font-mono text-[10px]">
                    {f.pill_id}
                  </span>
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums">
                  {f.views}
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums">
                  {fmtMs(f.avg_dwell_ms)}
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums">
                  {fmtMs(f.total_dwell_ms)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────

export function DemoExperience({
  thing,
  filters,
}: {
  /** When provided, drill-down click handlers activate; absent on global view. */
  thing?: string;
  /**
   * Fleet-wide filters (group → things list, tenant). Used by the
   * Analytics page; the device-detail page omits this since `thing`
   * already pins the scope to a single device.
   */
  filters?: AnalyticsFilters;
}) {
  const { days, setDays } = useDaysUrlState();
  // Keep the "Last refreshed N s ago" caption truthful by re-ticking
  // once a second — purely cosmetic, no extra fetches.
  useNow(1000);
  // Inline drill-down: clicking a bar / heatmap cell on the device
  // page expands a detail panel under the chart row showing that
  // day's sessions, rather than navigating away to a separate page.
  // Absent on /analytics (no thing context).
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  // Request twice the days so we can split into current / prior windows
  // for the KPI deltas. The chart and breakdown still only show the
  // current window — derived via slicing the response.
  const sessions = useAnalyticsSessions(days * 2, thing, filters);
  const views = useAnalyticsTopViews(days, thing, filters);
  const content = useAnalyticsContent(days, thing, filters);
  const products = useAnalyticsProducts(days, thing, filters);
  // Inline product drill-down — clicking a product row on the device
  // page expands a per-pill breakdown panel underneath the table.
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);

  // Backend may return only days that had at least one session, so we
  // can't slice by position — split by *date* so the two N-day windows
  // are accurate even when intermediate days are missing.
  const allDays = sessions.data?.sessions_by_day ?? [];
  const dayMs = 86_400_000;
  const nowMidnight = (() => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    return d.valueOf();
  })();
  const currentStart = nowMidnight - (days - 1) * dayMs;
  const priorStart = nowMidnight - (2 * days - 1) * dayMs;
  const currentDays = allDays.filter((r) => {
    const t = new Date(r.day).getTime();
    return Number.isFinite(t) && t >= currentStart;
  });
  const priorDays = allDays.filter((r) => {
    const t = new Date(r.day).getTime();
    return Number.isFinite(t) && t >= priorStart && t < currentStart;
  });
  // For the bar chart + heatmap we want every day in the window, with
  // zeros for days the API didn't return. ASC order: oldest left.
  const currentDaysAsc = fillDays(currentDays, days);

  const cur = totals(currentDays);
  const prior = totals(priorDays);
  const avgSessionS = cur.sessions ? Math.round(cur.activeS / cur.sessions) : 0;
  const priorAvgSessionS = prior.sessions
    ? Math.round(prior.activeS / prior.sessions)
    : 0;
  const feedbackRate = cur.sessions ? cur.feedback / cur.sessions : 0;
  const priorFeedbackRate = prior.sessions ? prior.feedback / prior.sessions : 0;

  // Cross-query refresh: each analytics hook is independent so a
  // single refetch wakes all four. isFetching across the union spins
  // the ↻ chip even on the natural 60 s background polls.
  const isFetching =
    sessions.isFetching ||
    views.isFetching ||
    content.isFetching ||
    products.isFetching;
  const refresh = () => {
    sessions.refetch();
    views.refetch();
    content.refetch();
    products.refetch();
  };

  // "Last refreshed" timestamp — use the freshest of the three. React
  // Query exposes dataUpdatedAt as a wall-clock ms; 0 means never.
  const lastUpdatedAt = Math.max(
    sessions.dataUpdatedAt,
    views.dataUpdatedAt,
    content.dataUpdatedAt,
    products.dataUpdatedAt,
  );
  const lastUpdatedLabel =
    lastUpdatedAt > 0 ? fmtAgo(new Date(lastUpdatedAt)) : '—';

  const drillToDay = thing
    ? (day: string) => setSelectedDay((day ?? '').slice(0, 10))
    : undefined;

  return (
    <section className="card">
      {/* Header — title + last-refreshed caption + range buttons + ↻. */}
      <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">
            Demo experience
          </h2>
          <p className="text-[10px] text-muted tabular-nums">
            updated {lastUpdatedLabel}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {VALID_DAYS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                days === d
                  ? 'bg-accent text-white'
                  : 'bg-soft text-muted hover:text-fg'
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            type="button"
            onClick={refresh}
            disabled={isFetching}
            title={isFetching ? 'Refreshing…' : 'Refresh now'}
            aria-label="Refresh analytics"
            className="ml-1 px-2 py-1 rounded-md text-xs font-medium bg-soft text-muted hover:text-fg disabled:opacity-50 leading-none"
          >
            <span
              className={`inline-block ${isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            >
              ↻
            </span>
          </button>
        </div>
      </div>

      {/* KPI BAND. Full-bleed hairlines (negative-margin to clear card
          padding) frame the band as a distinct zone. */}
      <div className="-mx-4 border-y border-border bg-soft/40">
        <div className="flex divide-x divide-border">
          <KPITile
            label="Sessions"
            value={cur.sessions.toString()}
            delta={deltaPct(cur.sessions, prior.sessions)}
            deltaHint={`vs previous ${days} d: ${prior.sessions} sessions`}
            deltaIntent="good-up"
          />
          <KPITile
            label="Active time"
            value={fmtDuration(cur.activeS)}
            delta={deltaPct(cur.activeS, prior.activeS)}
            deltaHint={`vs previous ${days} d: ${fmtDuration(prior.activeS)}`}
            deltaIntent="good-up"
          />
          <KPITile
            label="Avg session"
            value={fmtDuration(avgSessionS)}
            delta={deltaPct(avgSessionS, priorAvgSessionS)}
            deltaHint={`vs previous ${days} d: ${fmtDuration(priorAvgSessionS)}`}
            deltaIntent="good-up"
          />
          <KPITile
            label="Feedback rate"
            value={`${Math.round(feedbackRate * 100)} %`}
            hint={`${cur.feedback} / ${cur.sessions}`}
            delta={deltaPct(feedbackRate, priorFeedbackRate)}
            deltaHint={`vs previous ${days} d: ${Math.round(priorFeedbackRate * 100)} %`}
            deltaIntent="good-up"
          />
        </div>
      </div>

      {/* Chart row — Sessions per day fills the available width;
          Weekly pattern sits beside it at 30 d / 90 d and is hidden at
          7 d (where the bar chart already covers the same data). On
          narrow viewports they stack. */}
      <div className="mt-5 flex flex-col md:flex-row gap-6 items-start">
        <div className="md:flex-1 min-w-0 w-full">
          <h3 className="text-[10px] uppercase tracking-widest text-muted">
            Sessions per day
          </h3>
          {drillToDay && (
            <p className="text-[10px] text-muted mb-2">
              Click a bar to see that day&rsquo;s sessions.
            </p>
          )}
          {!drillToDay && <div className="mb-2" />}
          {sessions.isLoading && (
            <div className="h-[120px] bg-soft rounded animate-pulse" />
          )}
          {sessions.data && currentDaysAsc.length === 0 && (
            <p className="text-xs text-muted">No demo_session events yet.</p>
          )}
        {sessions.data && currentDaysAsc.length > 0 && (
          <ConsoleBarChart<SessionDay>
            height={120}
            data={currentDaysAsc.map((d) => ({
              label: d.day.slice(5, 10),
              value: d.sessions,
              meta: d,
            }))}
            tooltip={(item) => {
              const d = item.meta;
              const dayDate = new Date(d.day);
              const dayLabel = Number.isNaN(dayDate.getTime())
                ? d.day
                : dayDate.toLocaleDateString([], {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'short',
                  });
              return (
                <>
                  <div className="font-medium mb-1">{dayLabel}</div>
                  <div className="flex gap-3">
                    <span className="text-muted">Sessions</span>
                    <span className="ml-auto">{d.sessions}</span>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-muted">Active</span>
                    <span className="ml-auto">
                      {fmtDuration(Number(d.total_active_s))}
                    </span>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-muted">Views</span>
                    <span className="ml-auto">{d.total_views}</span>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-muted">Feedback</span>
                    <span className="ml-auto">{d.feedback_count}</span>
                  </div>
                </>
              );
            }}
            onBarClick={
              drillToDay ? (item) => drillToDay(item.meta.day) : undefined
            }
          />
        )}
        </div>

        {/* Weekly pattern — calendar heatmap. Companion to the bar
            chart; surfaces day-of-week / week-over-week rhythm at a
            glance. Hidden at 7 d where the bar chart already covers
            the same data (a 1-column heatmap reads as redundant). */}
        {days > 7 && (
          <div className="md:shrink-0">
            <h3 className="text-[10px] uppercase tracking-widest text-muted mb-2">
              Weekly pattern
            </h3>
            {sessions.isLoading && (
              <div className="h-[116px] w-48 bg-soft rounded animate-pulse" />
            )}
            {sessions.data && (
              <ActivityHeatmap
                days={currentDaysAsc}
                onCellClick={drillToDay}
              />
            )}
          </div>
        )}
      </div>

      {/* Inline session drill-down — fed by useDeviceSessions, opens
          under the chart row when a bar / heatmap cell is clicked.
          Skipped on the global /analytics page (no thing context). */}
      {thing && selectedDay && (
        <SessionDayPanel
          thing={thing}
          day={selectedDay}
          onClose={() => setSelectedDay(null)}
        />
      )}

      {/* Breakdown — Top views + Content engagement. */}
      <div className="mt-8 pt-4 border-t border-border -mx-1 flex flex-wrap gap-x-6 gap-y-4">
        <BreakdownTable<TopView>
          title="Top views by dwell"
          rows={views.data?.top_views ?? []}
          rowKey={(v) => v.view}
          loading={views.isLoading}
          emptyText="No view_visit events."
          columns={[
            {
              header: 'View',
              headerTitle: 'QML view name (Main menu, Product page, …).',
              mono: true,
              maxChars: 18,
              render: (v) => v.view,
            },
            {
              header: 'Visits',
              headerTitle: 'Number of times this view was entered in the window.',
              align: 'right',
              render: (v) => v.visits,
            },
            {
              header: 'Avg',
              headerTitle: 'Average dwell time per visit on this view.',
              align: 'right',
              render: (v) => fmtMs(v.avg_dwell_ms),
            },
            {
              header: 'Total',
              headerTitle: 'Cumulative dwell time across every visit on this view.',
              align: 'right',
              render: (v) => fmtMs(v.total_dwell_ms),
            },
          ]}
        />
        <BreakdownTable<ContentRow>
          title="Content engagement"
          rows={content.data?.content ?? []}
          rowKey={(c) => `${c.content_id}-${c.kind}`}
          loading={content.isLoading}
          emptyText="No content_play events."
          columns={[
            {
              header: 'Content',
              headerTitle: 'Content identifier — video / audio asset id.',
              mono: true,
              maxChars: 18,
              render: (c) => c.content_id,
            },
            {
              header: 'Plays',
              headerTitle: 'Number of times this content was started.',
              align: 'right',
              render: (c) => c.plays,
            },
            {
              header: 'Avg',
              headerTitle: 'Average watched time per play (watched_ms).',
              align: 'right',
              render: (c) => fmtMs(c.avg_watched_ms),
            },
            {
              header: 'Completion',
              headerTitle:
                'Share of plays that reached the end of the content (completed = true).',
              align: 'right',
              render: (c) => fmtPct(c.completion_rate),
            },
          ]}
        />
      </div>

      {/* Product engagement — web-store-style per-product breakdown
          (v2.1.0 typed events). Rows are clickable on the device page,
          expanding an inline feature drill-down beneath the table that
          shows which spec dialogs the customer opened. The compare
          caption surfaces compare_view dwell as a single signal without
          taking a full table row. */}
      <div className="mt-8 pt-4 border-t border-border -mx-1">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-[10px] uppercase tracking-widest text-muted">
            Product engagement
          </h3>
          {products.data?.compare && products.data.compare.views > 0 && (
            <p className="text-[10px] text-muted tabular-nums">
              Compare table —{' '}
              <span className="text-fg">
                {products.data.compare.views}
              </span>{' '}
              {products.data.compare.views === 1 ? 'view' : 'views'}, avg{' '}
              <span className="text-fg">
                {fmtMs(products.data.compare.avg_dwell_ms)}
              </span>
            </p>
          )}
        </div>
        {products.isLoading && (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonLine key={i} width={`${100 - i * 8}%`} />
            ))}
          </div>
        )}
        {products.data && products.data.products.length === 0 && (
          <p className="text-xs text-muted">
            No product_view events in this window.
          </p>
        )}
        {products.data && products.data.products.length > 0 && (
          <>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted border-b border-border">
                  <th className="font-medium py-1.5 pr-2">Product</th>
                  <th
                    className="font-medium py-1.5 pr-2 text-right cursor-help"
                    title="Times this product was on-screen in the carousel for ≥ 2 s (a deliberate look). Brief sweeps are excluded — see the per-row (+N) suffix or footnote below."
                  >
                    Impressions
                  </th>
                  <th
                    className="font-medium py-1.5 pr-2 text-right cursor-help"
                    title="Times the user opened this product's detail view."
                  >
                    Detail views
                  </th>
                  <th
                    className="font-medium py-1.5 pr-2 text-right cursor-help"
                    title="Average time spent on the detail view per visit."
                  >
                    Avg detail dwell
                  </th>
                  <th
                    className="font-medium py-1.5 pr-2 text-right cursor-help"
                    title="Feature popup opens (the spec / 'tell me more' dialog)."
                  >
                    Popups
                  </th>
                  <th
                    className="font-medium py-1.5 pr-2 text-right cursor-help"
                    title="Product image gallery opens."
                  >
                    Gallery
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {products.data.products.map((p: ProductRow) => {
                  const isSelected = selectedProduct === p.product_model;
                  return (
                    <Fragment key={p.product_model}>
                      <tr
                        onClick={() =>
                          setSelectedProduct(
                            isSelected ? null : p.product_model,
                          )
                        }
                        className={[
                          'border-b border-border cursor-pointer hover:bg-soft/60',
                          isSelected ? 'bg-soft/60' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <td className="py-1.5 pr-2 font-mono">{p.product_model}</td>
                        <td
                          className="py-1.5 pr-2 text-right tabular-nums"
                          title={
                            p.sweeps > 0
                              ? `${p.sweeps} brief pass-through${p.sweeps === 1 ? '' : 's'} (<2 s) excluded`
                              : undefined
                          }
                        >
                          {p.impressions}
                          {p.sweeps > 0 && (
                            <span className="text-muted text-[10px] ml-1">
                              (+{p.sweeps})
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">
                          {p.detail_views}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">
                          {fmtMs(p.avg_detail_dwell_ms)}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">
                          {p.popup_count}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">
                          {p.gallery_count}
                        </td>
                        <td className="py-1.5 text-right text-muted text-[11px]">
                          {isSelected ? '▼' : '▶'}
                        </td>
                      </tr>
                      {isSelected && (
                        <tr className="border-b border-border">
                          <td colSpan={7} className="p-0">
                            <ProductFeaturePanel
                              thing={thing}
                              productModel={p.product_model}
                              days={days}
                              filters={filters}
                              onClose={() => setSelectedProduct(null)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            {(() => {
              const totalSweeps = products.data.products.reduce(
                (n, p) => n + p.sweeps,
                0,
              );
              if (totalSweeps === 0) return null;
              return (
                <p className="text-[10px] text-muted mt-2 tabular-nums">
                  Impressions count carousel focus ≥2 s.{' '}
                  <span className="text-fg">{totalSweeps}</span>{' '}
                  brief pass-through{totalSweeps === 1 ? '' : 's'} excluded
                  (shown in parens per row).
                </p>
              );
            })()}
          </>
        )}
      </div>

    </section>
  );
}
